import { supabase } from '../lib/supabase';
import { DiagnosticFormData } from '../types/diagnostic';
import { OPTISECUR_COMPANY_ID } from '../config/constants';
import { getPendingDiagnostic, cleanupPendingDiagnostic } from '../utils/pendingDiagnosticStorage';
import { calculateSecurityScore } from '../utils/securityCalculator';
import { generateRecommendedActions } from './recommendationService';
import { bulkCreateActionsFromRecommendations } from './progressTrackingService';

export interface Vulnerability {
  label: string;
  risk: 'high' | 'medium' | 'low';
  description: string;
  recommendations: string[];
}

export interface RecommendedAction {
  id: string;
  title: string;
  category: 'protection' | 'surveillance' | 'dissuasion';
  priority: 'high' | 'medium' | 'low';
  estimatedCost: number;
  scoreImpact: number;
  description: string;
  selectedTier?: 'budget' | 'recommended' | 'premium';
  selectedPrice?: number;
}

export interface SavedDiagnostic {
  id: string;
  user_id: string;
  advisor_id: string | null;
  client_id: string | null;
  client_reference: string | null;
  invitation_sent_at: string | null;
  diagnostic_data: DiagnosticFormData;
  security_score: number;
  category_scores: {
    physicalProtection: number;
    surveillance: number;
    deterrence: number;
  };
  recommendations: RecommendedAction[];
  score_protection?: number;
  score_surveillance?: number;
  score_dissuasion?: number;
  completed_actions: string[];
  score_projected: number;
  report_paid: boolean;
  stripe_payment_intent_id: string | null;
  is_favorite: boolean;
  created_at: string;
  updated_at: string;
}

export async function getUserDiagnostics(userId: string): Promise<SavedDiagnostic[]> {
  try {
    const { data, error } = await supabase
      .from('diagnostics')
      .select('*')
      .eq('user_id', userId)
      .order('is_favorite', { ascending: false })
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching diagnostics:', error);
      return [];
    }

    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error('Unexpected error fetching diagnostics:', error);
    return [];
  }
}

export async function createDiagnostic(
  userId: string,
  formData: DiagnosticFormData,
  score: number,
  scoreProtection: number,
  scoreSurveillance: number,
  scoreDissuasion: number,
  recommendedActions: RecommendedAction[],
  companyId: string = OPTISECUR_COMPANY_ID,
  clientReference?: string | null,
  skipPayment: boolean = false,
  advisorId?: string | null
): Promise<SavedDiagnostic> {
  // 1. Créer le diagnostic
  const { data, error } = await supabase
    .from('diagnostics')
    .insert({
      user_id: userId,
      advisor_id: advisorId ?? null,
      company_id: companyId,
      client_reference: clientReference ?? null,
      diagnostic_data: formData,
      security_score: score,
      category_scores: {
        physicalProtection: scoreProtection,
        surveillance: scoreSurveillance,
        deterrence: scoreDissuasion,
      },
      recommendations: recommendedActions,
      score_protection: scoreProtection,
      score_surveillance: scoreSurveillance,
      score_dissuasion: scoreDissuasion,
      completed_actions: [],
      score_projected: score,
      report_paid: skipPayment,
      is_favorite: false
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating diagnostic:', error);
    throw new Error('Failed to save diagnostic');
  }

  // 2. CRÉER LE SNAPSHOT INITIAL
  const { error: snapshotError } = await supabase
    .from('score_snapshots')
    .insert({
      diagnostic_id: data.id,
      score: score,
      score_protection: scoreProtection,
      score_surveillance: scoreSurveillance,
      score_dissuasion: scoreDissuasion,
      snapshot_reason: 'initial',
      created_at: new Date().toISOString()
    });

  if (snapshotError) {
    console.error('Error creating initial snapshot:', snapshotError);
    // Ne pas bloquer la création du diagnostic si le snapshot échoue
  }

  return data;
}

export async function updateDiagnostic(
  diagnosticId: string,
  updates: Partial<Pick<SavedDiagnostic, 'is_favorite'>>
): Promise<SavedDiagnostic> {
  const { data, error } = await supabase
    .from('diagnostics')
    .update(updates)
    .eq('id', diagnosticId)
    .select()
    .single();

  if (error) {
    console.error('Error updating diagnostic:', error);
    throw new Error('Failed to update diagnostic');
  }

  return data;
}

export async function deleteDiagnostic(diagnosticId: string): Promise<void> {
  const { error } = await supabase
    .from('diagnostics')
    .delete()
    .eq('id', diagnosticId);

  if (error) {
    console.error('Error deleting diagnostic:', error);
    throw new Error('Failed to delete diagnostic');
  }
}

export async function toggleFavorite(diagnosticId: string, currentFavorite: boolean): Promise<void> {
  const { error } = await supabase
    .from('diagnostics')
    .update({ is_favorite: !currentFavorite })
    .eq('id', diagnosticId);

  if (error) {
    console.error('Error toggling favorite:', error);
    throw new Error('Failed to toggle favorite');
  }
}

export async function markReportAsPaid(
  diagnosticId: string,
  stripePaymentIntentId: string
): Promise<void> {
  const { error } = await supabase
    .from('diagnostics')
    .update({
      report_paid: true,
      stripe_payment_intent_id: stripePaymentIntentId
    })
    .eq('id', diagnosticId);

  if (error) {
    console.error('Error marking report as paid:', error);
    throw new Error('Failed to mark report as paid');
  }
}

export async function updateCompletedActions(
  diagnosticId: string,
  completedActions: string[],
  projectedScore: number
): Promise<SavedDiagnostic> {
  const { data, error } = await supabase
    .from('diagnostics')
    .update({
      completed_actions: completedActions,
      score_projected: projectedScore
    })
    .eq('id', diagnosticId)
    .select()
    .single();

  if (error) {
    console.error('Error updating completed actions:', error);
    throw new Error('Failed to update completed actions');
  }

  return data;
}

export async function getDiagnosticById(
  diagnosticId: string
): Promise<SavedDiagnostic | null> {
  const { data, error } = await supabase
    .from('diagnostics')
    .select('*')
    .eq('id', diagnosticId)
    .maybeSingle();

  if (error) {
    console.error('Error fetching diagnostic:', error);
    return null;
  }

  return data;
}

export async function savePendingDiagnosticForUser(userId: string): Promise<{
  success: boolean;
  diagnosticId?: string;
  error?: string
}> {
  try {
    const pendingFormData = getPendingDiagnostic();

    if (!pendingFormData) {
      return { success: false, error: 'No pending diagnostic found' };
    }

    const scoreData = calculateSecurityScore(pendingFormData);
    const recommendedActions = generateRecommendedActions(pendingFormData, scoreData.categoryScores);

    const diagnostic = await createDiagnostic(
      userId,
      pendingFormData,
      scoreData.score,
      scoreData.categoryScores.protection,
      scoreData.categoryScores.surveillance,
      scoreData.categoryScores.deterrence,
      recommendedActions
    );

    if (diagnostic?.id) {
      await bulkCreateActionsFromRecommendations(
        diagnostic.id,
        recommendedActions.map(action => ({
          id: action.id,
          title: action.title,
          category: action.category as 'protection' | 'surveillance' | 'dissuasion',
          priority: action.priority as 'high' | 'medium' | 'low',
          estimatedCost: action.estimatedCost,
          scoreImpact: action.scoreImpact,
          description: action.description
        }))
      );

      cleanupPendingDiagnostic();

      return { success: true, diagnosticId: diagnostic.id };
    }

    return { success: false, error: 'Failed to create diagnostic' };
  } catch (error) {
    console.error('Error saving pending diagnostic:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    };
  }
}

/**
 * Lie un client à un diagnostic lors de sa première connexion.
 * N'écrase pas un client_id déjà existant (WHERE client_id IS NULL).
 */
export async function linkClientToDiagnostic(
  diagnosticId: string,
  clientId: string
): Promise<void> {
  const { error } = await supabase
    .from('diagnostics')
    .update({ client_id: clientId })
    .eq('id', diagnosticId)
    .is('client_id', null);

  if (error) {
    console.error('Error linking client to diagnostic:', error);
  }
}

/**
 * Récupère le diagnostic lié à un client (par client_id).
 * Utilisé dans le dashboard client pour afficher son unique diagnostic.
 */
export async function getClientLinkedDiagnostic(
  clientId: string
): Promise<SavedDiagnostic | null> {
  const { data, error } = await supabase
    .from('diagnostics')
    .select('*')
    .eq('client_id', clientId)
    .maybeSingle();

  if (error) {
    console.error('Error fetching client linked diagnostic:', error);
    return null;
  }

  return data ?? null;
}